import { CreditCardType, DetailedSeatModel, VerifyVoucherResponse } from '@/services';
import { OfferModel, OfferValidationType } from '@/logic/services/offers';
import { createStore, stateSetterHandler } from '@alpha.apps/react-common';

import { PaymentGatewayModel } from '@/logic/services/cinemas';
import { SubmitVoucherResponse } from '@/services/vouchers/models';
import { SxProps } from '@mui/material';

export interface CardDialogPayloadModel {
  img: string;
  info: string;
  title: string;
}

export interface OfferLimitDialogPayloadModel extends CardDialogPayloadModel {
  mainTitle: string;
}

interface CardConfirmDialogPayloadModel extends CardDialogPayloadModel {
  handleOkayClick: () => void;
  handleContinueClick: () => void;
}

interface OfferLimitInfoDialogPayloadModel extends OfferLimitDialogPayloadModel {
  handleClick: () => void;
}

interface OfferLimitConfirmDialogPayloadModel extends OfferLimitDialogPayloadModel {
  handleExitClick: () => void;
  handleCloseClick: () => void;
  handleOkayClick: () => void;
}

export type DialogState = {
  dialog: DialogOptions;
};
export interface DialogOptions {
  wrapper?: boolean;
  isPhoneNumberForm?: boolean;
  isApplyOfferDialog?: boolean;
  applyOfferDialogPayload?: {
    offer: OfferModel;
    sessionId?: number;
    isAfterMidnight?: boolean;
    movieId?: number;
    movieSlug?: string;
    isThreeD?: boolean;
  };
  phoneNumberFormPayload?: any;
  selectedCountry?: any;
  isOtpForm?: boolean;
  isGuestOtpForm?: boolean;
  isStcVerificationCode?: boolean;
  stcNumber?: string;
  otpFormPayload?: any;
  isTermsAndConditionsForm?: boolean;
  termsAndConditionsFormPayload?: {
    handleBackwardClick: () => void;
  };
  isCompleteProfileForm?: boolean;
  isEmailOtpForm?: boolean;
  isEditProfileForm?: boolean;
  completeProfileFormPayload?: any;
  editProfileFormPayload?: any;
  isAvatarForm?: boolean;
  isAvatarFormPayload?: {
    isClose?: boolean;
    handleBackwardClick?: (data?: any) => void;
  };
  isOldUserForm?: boolean;
  oldUserFormPayload?: {
    email?: string;
    phoneNumber?: string;
    token: string;
  };

  emailResetCodeFormPayload?: any;

  isProfileChangeOptionsForm?: boolean;
  profileChangeOptionsFormPayload?: {
    choice: string;
  };
  isChangeEmailOtpForm?: boolean;
  changeEmailOtpFormPayload?: any;
  isOldUserChangeEmailForm?: boolean;
  oldUserChangeEmailFormPayload?: {
    token?: string;
    phoneNumber?: string;
    email?: string;
  };
  isOldUserEmailOtpForm?: boolean;
  oldUserEmailOtpFormPayload?: any;
  isOldUserChangeEmailOtpForm?: boolean;
  oldUserChangeEmailOtpFormPayload?: any;
  changePhoneNumberFormPayload?: any;
  isNewEmailForm?: boolean;
  newEmailFormPayload?: any;
  isChangeEmailResetCodeForm?: boolean;
  isChangePhoneNumberResetCodeForm?: boolean;
  changePhoneNumberResetCodeFormPayload?: any;
  isNewPhoneNumberForm?: boolean;
  newPhoneNumberFormPayload?: any;
  isChangePhoneNumberOtpForm?: boolean;
  isCitiesForm?: boolean;
  citiesFormPayload?: {
    isUser: boolean;
  };
  isChangeCityConfirmDialog?: boolean;
  changeCityConfirmDialogPayload?: {
    description?: string;
    handleOkayClick: () => void;
  };
  isRulesOfEntryDialog?: boolean;
  rulesOfEntryDialogPayload?: {
    sessionId: number;
    isAfterMidnight: boolean;
    isThreeD?: boolean;
    movieId: number;
    offerId?: string;
    // offerId?: number;
    movieSlug?: string;
    refetchFunction?: () => void;
  };
  isHouseRulesDialog?: boolean;
  isCancelSeatsSelectionConfirmDialog?: boolean;
  cancelSeatsSelectionConfirmDialogPayload?: {
    redirect: boolean;
    nextPath?: string;
    removeOnRouteChangeStartListener?: () => void;
  };
  isChangeExperienceConfirmDialog?: boolean;
  changeExperienceConfirmDialogPayload?: {
    handleOkayClick: () => void;
  };
  isChangeShowtimeConfirmDialog?: boolean;
  changeShowtimeConfirmDialogPayload?: {
    handleOkayClick: () => void;
  };
  isWheelChairConfirmDialog?: boolean;
  wheelChairConfirmDialogPayload?: {
    areaIndex: number;
    seat: DetailedSeatModel;
  };
  isCompanionConfirmDialog?: boolean;
  companionConfirmDialogPayload?: {
    areaIndex: number;
    seat: DetailedSeatModel;
  };
  isShareSeatsPlanDialog?: boolean;
  isReservedSeatsInfoDialog?: boolean;
  isSessionExpiredInfoDialog?: boolean;
  sessionExpiredInfoDialogPayload?: {
    slug: string;
  };
  isDeleteAccountDialog?: boolean;
  isAllowedToChangeShowTimesDialog?: boolean;
  isAllowedToChangeShowTimesDialogPayload?: {
    isAllowed?: boolean;
    date?: string;
    timeDuration?: string;
  };
  isAllowedToClearAllFiltersDialog?: boolean;
  isAllowedToClearAllFiltersDialogPayload?: {
    isAllowed?: boolean;
  };
  isDeleteCardConfirmationDialog?: boolean;
  deleteCardConfirmationDialogPayload?: {
    handleOkayClick?: () => void;
    isLoading?: boolean;
  };
  isDeleteVoucherConfirmDialog?: boolean;
  deleteVoucherConfirmDialogPayload?: {
    handleSuccess?: (data: SubmitVoucherResponse) => void;
    barCode: string;
    orderId: string;
  };
  isApplyMultiVoucherDialog?: boolean;
  applyMultiVoucherDialogPayload?: {
    initialState: VerifyVoucherResponse;
    handleSubmitVoucher: (request: number) => void;
    barCode: string;
    isSubmitting?: boolean;
  };
  isAddCreditCardForm?: boolean;
  addCreditCardFormPayload?: {
    isBookingFlow: boolean;
    paymentGateway?: PaymentGatewayModel;
    title?: string;
  };
  isEditCreditCardForm?: boolean;
  editCreditCardFormPayload?: {
    id: number;
    cardName: string;
    cardNumber: string;
    cardHolder: string;
    expiryDate: string;
    cardType: CreditCardType;
    isDefault: boolean;
  };
  isNotificationPopover?: boolean;
  isClearHistoryConfirmDialog?: boolean;
  isNotAvailableOfferInfoDialog?: boolean;
  notAvailableOfferInfoDialogPayload?: {
    img: string;
    logoUrlAlt: string;
    bankName: string;
    title: string;
    hint: string;
    validationType: OfferValidationType;
  };
  isLogoutConfirmDialog?: boolean;
  isDeleteOfferConfirmDialog?: boolean;
  deleteOfferConfirmDialogPayload?: {
    handleDelete: () => void;
  };

  isCardNotSameVerifiedConfirmDialog?: boolean;
  cardNotSameVerifiedConfirmDialogPayload?: CardConfirmDialogPayloadModel;
  isCardNotSupportedConfirmDialog?: boolean;
  cardNotSupportedConfirmDialogPayload?: CardConfirmDialogPayloadModel;
  isCardNotSameAppliedConfirmDialog?: boolean;
  cardNotSameAppliedConfirmDialogPayload?: CardConfirmDialogPayloadModel;
  isOfferLimitInfoDialog?: boolean;
  offerLimitInfoDialogPayload?: OfferLimitInfoDialogPayloadModel;
  isOfferLimitConfirmDialog?: boolean;
  offerLimitConfirmDialogPayload?: OfferLimitConfirmDialogPayloadModel;

  isOfflineDialog?: boolean;
  isExperienceInfoDialog?: boolean;
  experienceInfoDialogPayload?: unknown;
  minWidth?: SxProps;
  tabletMinHeight?: string;
  isAdBlockDialog?: boolean;
  isShowReportButton?: boolean;
  isReportMuviDialog?: boolean;
  reportMuviDialogPayload?: {
    data: { email?: string; phoneNumber?: string; subject?: string };
    handleBackwardClick: () => void;
  };
  uploadedProfileAvatar?: undefined | File;
  isWalletExpiryDetailsDialog?: boolean;
  WalletExpiryDetailsDialogPayload?: {
    isCashback?: boolean;
  };
  isAboutWalletDialog?: boolean;
  isAboutCashbackDialog?: boolean;
  isAddTopUpDialog?: boolean;
  isWalletRemainDialog?: boolean;
  isCashbackRemainDialog?: boolean;
  isCancelTicketConfirmDialog?: boolean;
  cancelTicketConfirmDialogPayload?: {
    cancellationReasonId: number;
  };
  isWalletOtpEmailForm?: boolean;
  isChangeEmailConfirmDialog?: boolean;
  isLogoutTimerDialog?: boolean;
}

const initState: DialogState = {
  dialog: {
    wrapper: false,
    isPhoneNumberForm: false,
    isApplyOfferDialog: false,
    phoneNumberFormPayload: {},
    selectedCountry: {},
    isOtpForm: false,
    otpFormPayload: {},
    isTermsAndConditionsForm: false,
    isCompleteProfileForm: false,
    isEmailOtpForm: false,
    isEditProfileForm: false,
    editProfileFormPayload: {},
    isAvatarForm: false,
    isAvatarFormPayload: {
      isClose: null,
    },
    isOldUserForm: false,
    oldUserFormPayload: {
      token: '',
    },

    isProfileChangeOptionsForm: false,
    profileChangeOptionsFormPayload: {
      choice: '',
    },
    isChangeEmailOtpForm: false,
    isOldUserChangeEmailForm: false,
    oldUserChangeEmailFormPayload: { token: '', phoneNumber: '', email: '' },
    changeEmailOtpFormPayload: {},
    isOldUserEmailOtpForm: false,
    oldUserEmailOtpFormPayload: {},
    isOldUserChangeEmailOtpForm: false,
    oldUserChangeEmailOtpFormPayload: {},
    changePhoneNumberFormPayload: {},
    isNewEmailForm: false,
    newEmailFormPayload: {},
    isChangeEmailResetCodeForm: false,
    isChangePhoneNumberResetCodeForm: false,
    changePhoneNumberResetCodeFormPayload: {},
    isNewPhoneNumberForm: false,
    newPhoneNumberFormPayload: {},
    isChangePhoneNumberOtpForm: false,
    isCitiesForm: false,
    citiesFormPayload: {
      isUser: false,
    },
    isChangeCityConfirmDialog: false,
    isRulesOfEntryDialog: false,
    isHouseRulesDialog: false,
    isCancelSeatsSelectionConfirmDialog: false,
    isChangeExperienceConfirmDialog: false,
    isChangeShowtimeConfirmDialog: false,
    isWheelChairConfirmDialog: false,
    isCompanionConfirmDialog: false,
    isShareSeatsPlanDialog: false,
    isReservedSeatsInfoDialog: false,
    isSessionExpiredInfoDialog: false,
    isDeleteAccountDialog: false,
    isAllowedToChangeShowTimesDialog: false,
    isAllowedToChangeShowTimesDialogPayload: {
      isAllowed: false,
      date: '',
      timeDuration: '',
    },
    isAllowedToClearAllFiltersDialog: false,
    isAllowedToClearAllFiltersDialogPayload: {
      isAllowed: false,
    },
    isDeleteCardConfirmationDialog: false,
    deleteCardConfirmationDialogPayload: {},
    isDeleteVoucherConfirmDialog: false,
    deleteVoucherConfirmDialogPayload: {
      barCode: '',
      orderId: '',
    },
    isApplyMultiVoucherDialog: false,
    isAddCreditCardForm: false,
    isEditCreditCardForm: false,
    isClearHistoryConfirmDialog: false,
    isNotAvailableOfferInfoDialog: false,
    isLogoutConfirmDialog: false,
    isOfflineDialog: false,
    isExperienceInfoDialog: false,
    experienceInfoDialogPayload: {},
    minWidth: null,
    tabletMinHeight: null,
    isAdBlockDialog: false,
    isShowReportButton: false,
    isReportMuviDialog: false,
    uploadedProfileAvatar: null,
    reportMuviDialogPayload: {
      data: null,
      handleBackwardClick: null,
    },
    isWalletExpiryDetailsDialog: false,
    isAboutWalletDialog: false,
    isAboutCashbackDialog: false,
  },
};

export const useDialog = createStore({
  name: 'user-flow-dialog',
  devtools: true,
  persist: false,
})(initState, set => {
  const handler = stateSetterHandler(set);
  return {
    setDialog: handler('dialog'),
  };
});
